import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search, LayoutGrid, CheckSquare, Briefcase, Paperclip, MessageSquare, ChevronDown, Plus, X,
  CornerDownLeft, Loader2, Check,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import api from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { recentTokens, recordVisit } from '../../lib/recentlyVisited';

// Monday-style "Search for anything" window — Cmd+B (Mac) / Ctrl+B anywhere in
// the app, or the search button in the header. Searches only what the user
// can open (GET /boards/quick-search applies the board access rules).

const TYPES = [
  { key: 'boards', label: 'Boards', icon: LayoutGrid },
  { key: 'items', label: 'Items', icon: CheckSquare },
  { key: 'workspaces', label: 'Workspaces', icon: Briefcase },
];
const MORE = [
  { key: 'files', label: 'Files', icon: Paperclip },
  { key: 'comments', label: 'Comments', icon: MessageSquare },
];
const SECTION = { boards: 'Boards', items: 'Items', workspaces: 'Workspaces', files: 'Files', comments: 'Comments' };
const ORDER = ['boards', 'items', 'workspaces', 'files', 'comments'];
const NO_SEARCH_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/join', '/f/'];
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
export const SHORTCUT_LABEL = IS_MAC ? '⌘B' : 'Ctrl+B';

const WS_COLOURS = ['#e2445c', '#ff642e', '#fdab3d', '#00c875', '#037f4c', '#579bfc', '#0086c0', '#a25ddc', '#784bd1', '#ff5ac4'];
const wsColour = (name) => {
  let h = 0;
  for (const ch of String(name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return WS_COLOURS[h % WS_COLOURS.length];
};

const inEditor = (el) => !!el && (el.isContentEditable || !!el.closest?.('[contenteditable="true"]'));

const Highlight = ({ text, query }) => {
  const s = String(text || '');
  const q = (query || '').trim().toLowerCase();
  const at = q ? s.toLowerCase().indexOf(q) : -1;
  if (at < 0) return s;
  return (
    <>
      {s.slice(0, at)}
      <span className="font-semibold text-gray-900">{s.slice(at, at + q.length)}</span>
      {s.slice(at + q.length)}
    </>
  );
};

const ResultIcon = ({ hit }) => {
  if (hit.type === 'workspace') {
    return (
      <span className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
        style={{ backgroundColor: wsColour(hit.title) }}>
        {(hit.title || '?').trim().charAt(0).toUpperCase()}
      </span>
    );
  }
  const Icon = { board: LayoutGrid, item: CheckSquare, file: Paperclip, comment: MessageSquare }[hit.type] || Search;
  return (
    <span className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 text-gray-500 border border-gray-200 bg-white">
      <Icon className="h-4 w-4" />
    </span>
  );
};

const Crumbs = ({ hit }) => {
  const parts = hit.type === 'workspace' ? ['Workspace'] : (hit.path || []);
  return (
    <div className="flex items-center gap-1 text-xs text-gray-500 min-w-0 truncate">
      {hit.type === 'comment' && hit.author && <span className="flex-shrink-0">{hit.author} ·</span>}
      {hit.type === 'item' && hit.subitem && <span className="flex-shrink-0">Sub-item ·</span>}
      <span className="truncate">{parts.join(' › ')}</span>
    </div>
  );
};

const QuickSearch = () => {
  const { user } = useAuth();
  const { workspaces, currentWorkspace, setCurrentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [type, setType] = useState(null);          // null = everything
  const [wsFilter, setWsFilter] = useState(null);  // { id, name }
  const [results, setResults] = useState({});
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const listRef = useRef(null);

  const enabled = !!user && !NO_SEARCH_PATHS.some((p) => location.pathname.startsWith(p))
    && (() => { try { return sessionStorage.getItem('embed_mode') !== '1'; } catch { return true; } })();

  // Cmd/Ctrl+B anywhere (except inside the comment editor, where it means bold),
  // and the header's search button.
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === 'b' || e.key === 'B')) {
        if (inEditor(document.activeElement)) return;
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('aflo:open-search', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('aflo:open-search', onOpen);
    };
  }, [enabled]);

  // Workspaces count as visited when they're switched to.
  useEffect(() => {
    if (currentWorkspace?.id) recordVisit(user?.id, 'workspace', currentWorkspace.id);
  }, [currentWorkspace?.id, user?.id]);

  // A fresh window each time it opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    setResults({});
  }, [open]);

  // Recently visited (resolved by the API: fresh names, and anything no longer
  // accessible is dropped).
  useEffect(() => {
    if (!open || !user?.id) return undefined;
    const tokens = recentTokens(user.id);
    if (!tokens.length) { setRecent([]); return undefined; }
    let cancelled = false;
    api.get('/boards/quick-search', { params: { recent: tokens.join(',') } })
      .then((r) => { if (!cancelled) setRecent((r.data.recent || []).slice(0, 8)); })
      .catch(() => { if (!cancelled) setRecent([]); });
    return () => { cancelled = true; };
  }, [open, user?.id]);

  // Search as you type (debounced; an older request never overwrites a newer one).
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (!q) { setResults({}); setLoading(false); return undefined; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      api.get('/boards/quick-search', {
        params: { q, types: type || undefined, workspace_id: wsFilter?.id || undefined, limit: type ? 20 : 6 },
        signal: ctrl.signal,
      })
        .then((r) => { setResults(r.data.results || {}); setActive(0); })
        .catch(() => { if (!ctrl.signal.aborted) setResults({}); })
        .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    }, 180);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [open, query, type, wsFilter]);

  const q = query.trim();
  const sections = useMemo(() => {
    if (!q) return recent.length ? [{ key: 'recent', title: 'Recently visited', hits: recent }] : [];
    return ORDER.filter((k) => (results[k] || []).length).map((k) => ({ key: k, title: SECTION[k], hits: results[k] }));
  }, [q, recent, results]);
  const flat = useMemo(() => sections.flatMap((s) => s.hits), [sections]);

  useEffect(() => { if (active >= flat.length) setActive(0); }, [flat.length, active]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const go = useCallback((hit, newTab = false) => {
    if (!hit) return;
    if (hit.type === 'workspace') {
      const ws = (workspaces || []).find((w) => w.id === hit.id);
      if (ws) setCurrentWorkspace(ws);
      setOpen(false);
      navigate('/workspaces');
      return;
    }
    if (newTab) {
      window.open(hit.url, '_blank', 'noopener');
      return;
    }
    setOpen(false);
    navigate(hit.url);
  }, [workspaces, setCurrentWorkspace, navigate]);

  const onKeyDown = (e) => {
    // Only from the search box or a result — buttons/menus keep their own keys.
    if (!e.target.closest?.('[data-testid="quick-search-input"], [data-testid="quick-search-hit"]')) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (flat.length ? (a + 1) % flat.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (flat.length ? (a - 1 + flat.length) % flat.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(flat[active], e.metaKey || e.ctrlKey);
    }
  };

  if (!enabled) return null;

  const moreActive = MORE.find((m) => m.key === type);
  const chip = (on) => `h-9 px-3.5 rounded-full border text-sm flex items-center gap-2 transition-colors ${
    on ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`;
  let idx = -1;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-[720px] w-[calc(100vw-2rem)] p-0 gap-0 top-[8vh] translate-y-0 overflow-hidden rounded-2xl [&>button:last-child]:hidden"
        onKeyDown={onKeyDown}
        data-testid="quick-search"
      >
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="px-6 pt-5 pb-4 border-b border-gray-200 space-y-4">
          <div className="flex items-center gap-3">
            <Search className="h-5 w-5 text-gray-500 flex-shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for anything"
              className="flex-1 min-w-0 text-xl bg-transparent text-gray-900 placeholder:text-gray-400 focus:outline-none"
              aria-label="Search for anything"
              data-testid="quick-search-input"
            />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {TYPES.map((t) => (
              <button key={t.key} type="button" className={chip(type === t.key)}
                onClick={() => setType(type === t.key ? null : t.key)} data-testid={`quick-search-type-${t.key}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
            <Popover open={moreOpen} onOpenChange={setMoreOpen}>
              <PopoverTrigger asChild>
                <button type="button" className={chip(!!moreActive)}>
                  {moreActive ? <><moreActive.icon className="h-4 w-4" /> {moreActive.label}</> : 'More'}
                  <ChevronDown className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-44 p-1">
                {MORE.map((m) => (
                  <button key={m.key} type="button"
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-left"
                    onClick={() => { setType(type === m.key ? null : m.key); setMoreOpen(false); }}>
                    <m.icon className="h-4 w-4 text-gray-500" /> <span className="flex-1">{m.label}</span>
                    {type === m.key && <Check className="h-4 w-4 text-orange-600" />}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-medium tracking-wide text-gray-500">FILTERS</span>
            {wsFilter ? (
              <span className="h-8 pl-3 pr-1.5 rounded-full border border-orange-300 bg-orange-50 text-orange-700 text-sm flex items-center gap-1.5">
                Workspace: {wsFilter.name}
                <button type="button" onClick={() => setWsFilter(null)} className="p-0.5 rounded-full hover:bg-orange-100"
                  aria-label="Remove workspace filter"><X className="h-3.5 w-3.5" /></button>
              </span>
            ) : (
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <button type="button"
                    className="h-8 px-3 rounded-full border border-dashed border-gray-300 text-sm text-gray-700 flex items-center gap-1.5 hover:bg-gray-50"
                    data-testid="quick-search-add-filter">
                    <Plus className="h-4 w-4" /> Add filter
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 p-1">
                  <div className="px-2 py-1.5 text-xs font-medium text-gray-500">Workspace</div>
                  <div className="max-h-64 overflow-y-auto">
                    {(workspaces || []).map((w) => (
                      <button key={w.id} type="button"
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-gray-100 text-left"
                        onClick={() => { setWsFilter({ id: w.id, name: w.name }); setFilterOpen(false); }}>
                        <span className="h-5 w-5 rounded flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0"
                          style={{ backgroundColor: wsColour(w.name) }}>{(w.name || '?').charAt(0).toUpperCase()}</span>
                        <span className="truncate">{w.name}</span>
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        <div ref={listRef} className="max-h-[55vh] overflow-y-auto px-3 py-3" data-testid="quick-search-results">
          {sections.map((s) => (
            <div key={s.key} className="mb-2">
              <div className="px-3 pt-1 pb-2 text-sm font-medium text-gray-600">{s.title}</div>
              {s.hits.map((hit) => {
                idx += 1;
                const i = idx;
                const on = i === active;
                return (
                  <button
                    key={`${hit.type}:${hit.id}`}
                    type="button"
                    data-idx={i}
                    onMouseMove={() => setActive(i)}
                    onClick={(e) => go(hit, e.metaKey || e.ctrlKey)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left ${on ? 'bg-gray-100' : ''}`}
                    data-testid="quick-search-hit"
                  >
                    <ResultIcon hit={hit} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-[15px] text-gray-800 truncate ${hit.type === 'comment' ? 'italic' : ''}`}>
                        <Highlight text={hit.title} query={q} />
                        {hit.archived && <span className="ml-2 text-xs not-italic text-gray-500 border border-gray-300 rounded px-1">Archived</span>}
                      </div>
                      <Crumbs hit={hit} />
                    </div>
                    {on && (
                      <span className="h-6 w-7 rounded-md border border-gray-300 flex items-center justify-center text-gray-500 flex-shrink-0">
                        <CornerDownLeft className="h-3.5 w-3.5" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
          {!sections.length && (
            <div className="px-3 py-10 text-center text-sm text-gray-500">
              {q
                ? (loading ? 'Searching…' : <>No results for “{q}”{type ? ` in ${SECTION[type].toLowerCase()}` : ''}{wsFilter ? ` in ${wsFilter.name}` : ''}</>)
                : 'Search boards, items and workspaces you have access to'}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex items-center gap-5 text-xs text-gray-500 flex-wrap">
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded border border-gray-300 bg-white font-sans">↑</kbd>
            <kbd className="px-1.5 py-0.5 rounded border border-gray-300 bg-white font-sans">↓</kbd> to navigate
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded border border-gray-300 bg-white font-sans">↵</kbd> to select
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded border border-gray-300 bg-white font-sans">{IS_MAC ? '⌘' : 'Ctrl'} ↵</kbd> new tab
          </span>
          <span className="flex items-center gap-1.5">
            <kbd className="px-1.5 py-0.5 rounded border border-gray-300 bg-white font-sans">esc</kbd> to close
          </span>
          <span className="ml-auto">{SHORTCUT_LABEL} to open anywhere</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickSearch;
